import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import ErrorReporter from "@/components/ErrorReporter";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useTelegram } from "@/hooks/useTelegram";
import { useTheme } from "@/hooks/useTheme";
import { useBrandTokens } from "@/hooks/useBrandTokens";
import { useKeyboardScrollFix } from "@/hooks/useKeyboardScrollFix";
import { useChildren } from "@/hooks/useChildren";
import { useChildDetail } from "@/hooks/useChildDetail";
import { formatMoney } from "@/lib/format";
import type { Child } from "@/types";
import type { NewsItem } from "@/types/news.types";
import { AmbientBackground } from "@/layout/AmbientBackground";
import { AppHeader } from "@/layout/AppHeader";
import { BottomNav, type AppTab } from "@/layout/BottomNav";
// import {
//   HomeSegment,
//   // HomeSegment,
//   type HomeSection,
// } from "@/layout/HomeSegment";
// import { BazaarView } from "@/features/bazaar";
import {
  FilterTabs,
  ChildList,
  ChildDetailPage,
  type TypeNeedFilterValue,
} from "@/features/children";
import { NewsList, NewsDetailPage } from "@/features/news";
import {
  ProfilePage,
  CreateApplicationPage,
  MyApplicationsPage,
} from "@/features/profile";
import { Toaster } from "@/components/ui/sonner";
import { SegmentedTabs } from "@/components/kit";
import { IS_CLICK_DOMAIN, stripLaunchParamsFromUrl } from "@/lib/env";
import { RETURNED_TO_BAZAAR } from "@/lib/returnFlags";

export default function App() {
  const { t } = useTranslation();
  const { user, initDataRaw, isReady } = useTelegram();
  // Root darajada chaqiriladi — useEffect orqali <html data-theme="..."> qo'yiladi
  const { resolvedTheme } = useTheme();
  useBrandTokens(resolvedTheme);
  // Mobil klaviatura chiqqanda fokusdagi inputni avtomatik ko'rinadigan qiladi.
  useKeyboardScrollFix();

  // KO'RINADIGAN viewport balandligini aniqlab `--app-height` CSS var'iga yozamiz.
  // Asosiy shell (fixed + ichki scroll) aynan shu balandlikni oladi. `inset:0`/
  // `100vh` iOS'da LAYOUT viewport'ni beradi — u ko'rinadigandan baland bo'lib,
  // nav ekran ostida ko'rinmay qolardi. `visualViewport.height` — eng aniq manba,
  // bo'lmasa `innerHeight`. Orientation/resize/chrome o'zgarishida yangilanadi.
  useEffect(() => {
    const setAppHeight = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        "--app-height",
        `${Math.round(h)}px`,
      );
    };
    setAppHeight();
    window.visualViewport?.addEventListener("resize", setAppHeight);
    window.addEventListener("resize", setAppHeight);
    window.addEventListener("orientationchange", setAppHeight);
    return () => {
      window.visualViewport?.removeEventListener("resize", setAppHeight);
      window.removeEventListener("resize", setAppHeight);
      window.removeEventListener("orientationchange", setAppHeight);
    };
  }, []);

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState<AppTab>("home");
  const [completedTab, setCompletedTab] = useState<"closed" | "helped">(
    "closed",
  );
  // Home tab ichidagi segment: bemor bolalar yoki maktab bozori (mavzuli yig'im).
  // To'lovdan ?bazaar=1 bilan qaytilsa darhol "bazaar" da ochiladi (boshlang'ich
  // qiymat modul yuklanishida olingan — flash/tartib muammosisiz).
  // const [homeSection, setHomeSection] = useState<HomeSection>(
  //   RETURNED_TO_BAZAAR ? "bazaar" : "children",
  // );

  /* ── Children feature ── */
  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] =
    useState<TypeNeedFilterValue>("barchasi");
  const typeNeedId =
    typeof activeFilter === "number" ? activeFilter : undefined;

  const { children, isLoading, isLoadingMore, hasMore, loadMore } = useChildren(
    { typeNeedId },
  );
  const { total: urgentCount } = useChildren({ typeNeedId: 1 });
  // useMemo: har renderni yangi array reference yaratmaslik uchun
  const completedStatusIds = useMemo(
    () => (completedTab === "closed" ? [4] : [7]),
    [completedTab],
  );
  const {
    children: completedChildren,
    isLoading: isCompletedLoading,
    isLoadingMore: isCompletedLoadingMore,
    hasMore: hasCompletedMore,
    loadMore: loadCompletedMore,
  } = useChildren({ statusIds: completedStatusIds });
  const { child: detailChild, isLoading: isDetailLoading } = useChildDetail(
    selectedChildId,
    children,
  );

  /* ── Completed detail ── */
  const [selectedCompletedChildId, setSelectedCompletedChildId] = useState<
    number | null
  >(null);
  const { child: completedDetailChild, isLoading: isCompletedDetailLoading } =
    useChildDetail(selectedCompletedChildId, completedChildren);

  /* ── News feature ── */
  const [selectedNewsId, setSelectedNewsId] = useState<number | null>(null);

  /* ── Profile sub-navigation ── */
  type ProfileView = "main" | "create-app" | "my-apps";
  const [profileView, setProfileView] = useState<ProfileView>("main");

  /* ── Stats (AppHeader uchun jami yig'ilgan) ── */
  const totalCollected = useMemo(
    () => children.reduce((s, c) => s + c.collected, 0),
    [children],
  );

  /* ── Dev block (special users) ── */
  const specialUsers = [
    99281932, 8173547492, 1073447152, 757851321, 598192448, 1957535046,
    654078780, 703586573,
  ];
  const isSpecialUser = specialUsers.includes(user?.id ?? 0);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncPatientGoInUrl = useCallback((id: number | null) => {
    if (!IS_CLICK_DOMAIN || typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (id === null) {
      url.searchParams.delete("patientGo");
    } else {
      url.searchParams.set("patientGo", String(id));
    }
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);

  /**
   * Mini app webview (ayniqsa iOS) da blur/fixed fon + translate animatsiya
   * birga ishlaganda jank bo'lishi mumkin. Shu qurilmalarda yengil (opacity-only)
   * transitionga tushamiz.
   */
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);
  const useLiteAnimations = prefersReducedMotion || isIOS;

  const pageVariants = useMemo(
    () =>
      useLiteAnimations
        ? {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
          }
        : {
            initial: { opacity: 0, x: 20 },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: -20 },
          },
    [useLiteAnimations],
  );

  const detailVariants = useMemo(
    () =>
      useLiteAnimations
        ? {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
          }
        : {
            initial: { opacity: 0, x: 30 },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: -30 },
          },
    [useLiteAnimations],
  );

  const pageTransition = useMemo(
    () =>
      useLiteAnimations
        ? { duration: 0.14, ease: "linear" as const }
        : { duration: 0.2, ease: "easeOut" as const },
    [useLiteAnimations],
  );

  const detailTransition = useMemo(
    () =>
      useLiteAnimations
        ? { duration: 0.16, ease: "linear" as const }
        : { duration: 0.22, ease: "easeInOut" as const },
    [useLiteAnimations],
  );

  const handleCopyInitData = useCallback(() => {
    if (!initDataRaw) return;
    navigator.clipboard.writeText(initDataRaw).then(() => {
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2500);
    });
  }, [initDataRaw]);

  // Click super app return URL: https://click.insonuchun.uz/?patientGo=<id>
  // App ochilganda shu query bo'lsa detail sahifani avtomatik ochamiz.
  useEffect(() => {
    if (!IS_CLICK_DOMAIN || typeof window === "undefined") return;
    const param = new URLSearchParams(window.location.search).get("patientGo");
    if (!param) return;
    const id = Number(param);
    if (Number.isFinite(id) && id > 0) {
      setSelectedChildId(id);
      setActiveTab("home");
    }
  }, []);

  // Click/Payme to'lovdan `BAZAAR_RETURN_URL` (.../?bazaar=1) orqali qaytganda:
  // qaror modul-const (RETURNED_TO_BAZAAR) dan olinadi — boshlang'ich state
  // allaqachon "bazaar", bu yerda esa faqat tab ni ta'minlab, paramni URL dan
  // tozalaymiz (refresh/ulashishda qayta ochilib qolmasin).
  useEffect(() => {
    if (!RETURNED_TO_BAZAAR) return;
    // setHomeSection("bazaar");
    setActiveTab("home");
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("bazaar") == null) return;
    params.delete("bazaar");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`,
    );
  }, []);

  // Super app launch paramlari (?lang, ?theme) boot paytida allaqachon o'qildi
  // va localStorage'ga saqlandi (resolveStartupLang / getInitialTheme). Endi
  // ularni URL'dan tozalaymiz: reload/ulashishda foydalanuvchining app ichida
  // o'zgartirgan til/mavzu tanlovi ustidan qayta yozilib ketmasin.
  useEffect(() => {
    stripLaunchParamsFromUrl();
  }, []);

  /* ── Handlers ── */
  const handleSelectChild = useCallback(
    (child: Child) => {
      setSelectedChildId(child.id);
      syncPatientGoInUrl(child.id);
    },
    [syncPatientGoInUrl],
  );
  const handleBackFromChild = useCallback(() => {
    setSelectedChildId(null);
    syncPatientGoInUrl(null);
  }, [syncPatientGoInUrl]);
  const handleSelectCompletedChild = useCallback(
    (child: Child) => setSelectedCompletedChildId(child.id),
    [],
  );
  const handleBackFromCompletedChild = useCallback(
    () => setSelectedCompletedChildId(null),
    [],
  );
  const handleSelectNews = useCallback(
    (item: NewsItem) => setSelectedNewsId(item.id),
    [],
  );
  const handleBackFromNews = useCallback(() => setSelectedNewsId(null), []);
  const handleProfileNavigate = useCallback(
    (view: "main" | "create-app" | "my-apps") => setProfileView(view),
    [],
  );
  const handleProfileTabChange = useCallback(() => setProfileView("main"), []);

  /* ── Loading gate ── */
  if (!isReady) {
    return (
      <>
        <ErrorReporter />
        <LoadingScreen />
      </>
    );
  }

  /* ── Child detail (full screen, no bottom nav) ── */
  if (selectedChildId !== null) {
    return (
      <>
        <Toaster theme={resolvedTheme} richColors position="top-center" />
        <ErrorReporter />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`child-detail-${selectedChildId}`}
            variants={detailVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={detailTransition}
            style={{
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
            }}
          >
            <ChildDetailPage
              child={detailChild}
              isLoading={isDetailLoading}
              onBack={handleBackFromChild}
            />
          </motion.div>
        </AnimatePresence>
      </>
    );
  }

  /* ── Completed child detail (full screen, no payment) ── */
  if (selectedCompletedChildId !== null) {
    return (
      <>
        <Toaster theme={resolvedTheme} richColors position="top-center" />
        <ErrorReporter />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`completed-detail-${selectedCompletedChildId}`}
            variants={detailVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={detailTransition}
            style={{
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
            }}
          >
            <ChildDetailPage
              child={completedDetailChild}
              isLoading={isCompletedDetailLoading}
              onBack={handleBackFromCompletedChild}
              showPayment={false}
            />
          </motion.div>
        </AnimatePresence>
      </>
    );
  }

  /* ── Profile: Create Application (full screen) ── */
  if (activeTab === "profile" && profileView === "create-app") {
    return (
      <>
        <Toaster theme={resolvedTheme} richColors position="top-center" />
        <ErrorReporter />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key="create-app"
            variants={detailVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={detailTransition}
            style={{
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
            }}
          >
            <CreateApplicationPage
              onBack={() => setProfileView("main")}
              onViewApplications={() => setProfileView("my-apps")}
              onGoHome={() => {
                setProfileView("main");
                setActiveTab("home");
              }}
            />
          </motion.div>
        </AnimatePresence>
      </>
    );
  }

  /* ── Profile: My Applications (full screen) ── */
  if (activeTab === "profile" && profileView === "my-apps") {
    return (
      <>
        <Toaster theme={resolvedTheme} richColors position="top-center" />
        <ErrorReporter />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key="my-apps"
            variants={detailVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={detailTransition}
            style={{
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
            }}
          >
            <MyApplicationsPage
              onBack={() => setProfileView("main")}
              onCreate={() => setProfileView("create-app")}
            />
          </motion.div>
        </AnimatePresence>
      </>
    );
  }

  /* ── News detail (full screen, no bottom nav) ── */
  if (selectedNewsId !== null) {
    return (
      <>
        <Toaster theme={resolvedTheme} richColors position="top-center" />
        <ErrorReporter />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`news-detail-${selectedNewsId}`}
            variants={detailVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={detailTransition}
            style={{
              willChange: "transform, opacity",
              backfaceVisibility: "hidden",
            }}
          >
            <NewsDetailPage
              newsId={selectedNewsId}
              onBack={handleBackFromNews}
            />
          </motion.div>
        </AnimatePresence>
      </>
    );
  }

  /* ── Main shell with bottom nav ── */
  return (
    <>
      <Toaster theme={resolvedTheme} richColors position="top-center" />
      <ErrorReporter />
      {/*
        Shell: fixed + KO'RINADIGAN viewport balandligi (`--app-height`). Sahifa
        (window/body) HECH QACHON scroll bo'lmaydi — scroll faqat ichki
        konteynerda. Bu super-app webview'laridagi (Zoomrad/iOS) `fixed`
        bug'larining tub davosi: window scroll bo'lmagach, super app o'z
        chrome'ini animatsiya qilishga signal olmaydi va nav qimirlamaydi.
        Balandlik `--app-height`'dan olinadi (JS aniqlaydi) — `inset:0`/`100vh`
        iOS'da nav'ni ekran ostiga tushirib yuborardi. Fallback: `100dvh`.
      */}
      <div
        className="fixed inset-x-0 top-0 overflow-hidden"
        style={{
          height: "var(--app-height, 100dvh)",
          background: "var(--app-bg)",
        }}
      >
        <AmbientBackground />

        {/* Yagona scroller — kontent shu yerda aylanadi, nav esa tashqarida statik */}
        <div
          className="absolute inset-0 z-10 overflow-y-auto"
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "contain",
          }}
        >
          <div
            className="relative max-w-lg mx-auto px-4"
            // --bottom-nav-height ni BottomNav o'zi e'lon qiladi (yagona manba) —
            // kontent doim nav balandligi + 20px nafas bilan tugaydi, hech qachon
            // nav ortida qolib ketmaydi. Fallback: 65px = 60px kontent + 5px floor.
            style={{
              paddingBottom: "calc(var(--bottom-nav-height, 65px) + 20px)",
            }}
          >
            <AnimatePresence mode="sync" initial={false}>
              {activeTab === "home" && (
                <motion.div
                  key="home"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  style={{
                    willChange: "transform, opacity",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <AppHeader
                    userName={user?.first_name ?? null}
                    totalCollected={
                      isLoading ? "—" : formatMoney(totalCollected)
                    }
                  />
                  {isSpecialUser && (
                    <div
                      className="mt-3 mb-1 rounded-2xl px-3 py-2.5"
                      style={{
                        background: "var(--c-elevated-1)",
                        border: "1px solid var(--c-divider)",
                      }}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span
                          className="text-[10px] font-semibold uppercase tracking-widest"
                          style={{ color: "var(--c-text-faint)" }}
                        >
                          {t("devInitDataRaw")}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyInitData}
                          disabled={!initDataRaw}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all active:scale-95 ${
                            copied ? "border" : "border border-transparent"
                          }`}
                          style={
                            copied
                              ? {
                                  background: "var(--c-brand-tint-strong)",
                                  color: "var(--primary)",
                                  borderColor: "var(--c-brand-tint-strong)",
                                }
                              : {
                                  background: "var(--c-elevated-2)",
                                  color: "var(--c-text-muted)",
                                }
                          }
                        >
                          {copied ? (
                            <>
                              <svg
                                className="w-3 h-3"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.5}
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              {t("copied")}
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-3 h-3"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden
                              >
                                <rect
                                  x="9"
                                  y="9"
                                  width="13"
                                  height="13"
                                  rx="2"
                                />
                                <path
                                  strokeLinecap="round"
                                  d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                                />
                              </svg>
                              {t("copy")}
                            </>
                          )}
                        </button>
                      </div>
                      <p
                        className="text-[10px] font-mono leading-snug break-all line-clamp-2"
                        style={{ color: "var(--c-text-disabled)" }}
                      >
                        {initDataRaw || "—"}
                      </p>
                    </div>
                  )}
                  {/* <HomeSegment value={homeSection} onChange={setHomeSection} /> */}
                  {/* {homeSection === "bazaar" ? (
                    <BazaarView />
                  ) :  */}
                  {isLoading ? (
                    <LoadingScreen className="min-h-[40vh]" />
                  ) : (
                    <>
                      <FilterTabs
                        activeFilter={activeFilter}
                        onFilterChange={setActiveFilter}
                        urgentCount={urgentCount}
                      />
                      <ChildList
                        children={children}
                        activeFilter={activeFilter}
                        onChildSelect={handleSelectChild}
                        hasMore={hasMore}
                        onLoadMore={loadMore}
                        isLoadingMore={isLoadingMore}
                      />
                    </>
                  )}
                </motion.div>
              )}
              {activeTab === "news" && (
                <motion.div
                  key="news"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  style={{
                    willChange: "transform, opacity",
                    backfaceVisibility: "hidden",
                  }}
                >
                  {/* News header */}
                  <div className="pt-5 pb-2">
                    <h1
                      className="text-2xl font-bold"
                      style={{ color: "var(--c-text)" }}
                    >
                      {t("navNews")}
                    </h1>
                  </div>
                  <NewsList onNewsSelect={handleSelectNews} />
                </motion.div>
              )}

              {activeTab === "completed" && (
                <motion.div
                  key="completed"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  style={{
                    willChange: "transform, opacity",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <div className="pt-5 pb-3">
                    <h1
                      className="text-2xl font-bold"
                      style={{ color: "var(--c-text)" }}
                    >
                      {t("navCompleted")}
                    </h1>
                  </div>

                  <div className="mb-4">
                    <SegmentedTabs<"closed" | "helped">
                      value={completedTab}
                      onChange={setCompletedTab}
                      items={[
                        { id: "closed", label: t("closedCollections") },
                        { id: "helped", label: t("helpWasGiven") },
                      ]}
                    />
                  </div>

                  {isCompletedLoading ? (
                    <LoadingScreen className="min-h-[35vh]" />
                  ) : (
                    <ChildList
                      children={completedChildren}
                      activeFilter={completedTab}
                      onChildSelect={handleSelectCompletedChild}
                      hasMore={hasCompletedMore}
                      onLoadMore={loadCompletedMore}
                      isLoadingMore={isCompletedLoadingMore}
                      interactive={true}
                    />
                  )}
                </motion.div>
              )}

              {activeTab === "profile" && (
                <motion.div
                  key="profile"
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  style={{
                    willChange: "transform, opacity",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <ProfilePage onNavigate={handleProfileNavigate} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <BottomNav
          activeTab={activeTab}
          onTabChange={(tab) => {
            if (tab === "profile") handleProfileTabChange();
            setActiveTab(tab);
          }}
        />
      </div>
    </>
  );
}
